'use client';

import { CloseIcon, MicroPhoneIcon } from '@insta-club/components';
import { useEffect, useRef, useState } from 'react';
import SendFillIcon from 'src/libs/components/icons/send-fill';

function generateRandomString(length: number) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
}

declare global {
    interface Window {
        streamReference: MediaStream | null;
    }
}

interface Props {
    onRecordEnd: (audioURL: string, audioFile: File) => void;
    onDeleteRecord: () => void;
}

const getFileExtension = (mimeType: string): string => {
    if (mimeType.includes('webm')) return '.webm';
    if (mimeType.includes('mp4')) return '.mp4';
    if (mimeType.includes('mpeg') || mimeType.includes('mp3')) return '.mp3';
    return '.webm';
};

const getSupportedMimeType = (): string => {
    const types = ['audio/webm', 'audio/webm;codecs=opus', 'audio/mp4', 'audio/mpeg'];

    return types.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

export default function VoiceRecorder({ onRecordEnd, onDeleteRecord }: Props) {
    const mediaRecorder = useRef<MediaRecorder>(null);
    const [recording, setRecording] = useState(true);
    const isDeleting = useRef(false);

    let chunks: BlobPart[] = [];

    useEffect(() => {
        if (navigator?.mediaDevices?.getUserMedia) {
            navigator.mediaDevices.getUserMedia({ audio: true, video: false }).then(onSuccess, onError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSuccess = (stream: MediaStream) => {
        const mimeType = getSupportedMimeType();
        mediaRecorder.current = new MediaRecorder(stream, {
            mimeType,
            audioBitsPerSecond: 128000
        });
        mediaRecorder.current.onstop = onStop;
        mediaRecorder.current.ondataavailable = onDataAvailable;
        mediaRecorder.current.start();
    };

    const onError = (error: DOMException) => {
        console.log('getUserMedia error:', error);
        // Close the recorder if permission is denied
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            onDeleteRecord();
        }
    };

    const onStop = () => {
        if (!mediaRecorder.current) return;
        if (window.streamReference instanceof MediaStream) {
            window.streamReference.getAudioTracks().forEach((track: MediaStreamTrack) => {
                track.stop();
            });

            window.streamReference = null;
        }
        if (isDeleting.current) {
            isDeleting.current = false;
            return;
        }
        const blob = new Blob(chunks, { type: mediaRecorder.current.mimeType });
        const extension = getFileExtension(mediaRecorder.current.mimeType);
        const audioFile = new File([blob], generateRandomString(15) + extension, {
            type: mediaRecorder.current.mimeType
        });
        chunks = [];
        const audioURL = window.URL.createObjectURL(blob);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onRecordEnd && onRecordEnd(audioURL, audioFile);
    };

    const onDataAvailable = (e: BlobEvent) => {
        chunks.push(e.data);
    };

    const handleSaveRecord = () => {
        stopRecord();
    };

    const deleteRecord = () => {
        isDeleting.current = true;
        stopRecord();
        chunks = [];
        setRecording(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onDeleteRecord && onDeleteRecord();
    };

    const stopRecord = () => {
        if (recording && mediaRecorder.current) {
            mediaRecorder.current.stop();
            const tracks = mediaRecorder.current.stream?.getTracks();
            if (tracks) {
                tracks.forEach((track) => track.stop());
            }
        }
    };

    return (
        <div className='h-10 w-full overflow-hidden'>
            <div className='flex items-center justify-between gap-2'>
                {recording && (
                    <div className='flex items-center gap-2'>
                        <div
                            className='relative flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-red-500 text-white'
                            onClick={deleteRecord}
                        >
                            <CloseIcon width={10} height={10} />
                        </div>
                        <Timer />
                    </div>
                )}
                <div className='flex flex-1 justify-end'>
                    <div
                        className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-full'
                        onClick={handleSaveRecord}
                    >
                        {recording ? (
                            <SendFillIcon className='text-primary-500 rotate-180' width={28} height={28} />
                        ) : (
                            <MicroPhoneIcon width={28} height={28} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const Timer = () => {
    const [time, setTime] = useState(0);
    const [isRunning] = useState(true);

    useEffect(() => {
        let timerInterval: NodeJS.Timeout;
        if (isRunning) {
            timerInterval = setInterval(() => {
                setTime((prevTime) => prevTime + 10);
            }, 10);
        }
        return () => {
            clearInterval(timerInterval);
        };
    }, [isRunning]);

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60000);
        const seconds = Math.floor((time % 60000) / 1000);
        const milliseconds = Math.floor((time % 1000) / 10);
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')},${String(milliseconds).padStart(2, '0')}`;
    };

    return <span className='!font-mono'>{formatTime(time)}</span>;
};

